/**
 * A session's urgency, and the one rule it is derived by: the backend's
 * `_derive_urgency_level` (routes/admin.py), mirrored here for the session page, which has
 * the report but not the list's derived `urgency_level`.
 *
 * Each report differential carries a three-level `risk_level`; the session's urgency is the
 * highest risk across the top three entries — a high-risk diagnosis anywhere in that window
 * surfaces even when it is not ranked first. The ontology's "urgent" level is unreachable on
 * this path: the ddx runner collapses urgent and semi-urgent into "medium" before the report is
 * composed, and "medium" reads back as semi-urgent.
 */

import type { ReportDifferential, Urgency, UrgencyLevel } from "../api/types";

/** How many of the report's entries the session's urgency is taken over. */
export const TRIAGE_WINDOW = 3;

/** Hover text wherever a session's urgency is not known yet. */
export const URGENCY_PENDING_TITLE =
  "Derived from the report, which is composed when the consultation completes.";

const RISK_URGENCY: Record<NonNullable<ReportDifferential["risk_level"]>, Urgency> = {
  high: "emergency",
  medium: "semi_urgent",
  low: "non_urgent",
};

/** An entry's risk level on the acuity scale; `null` when it carried none. */
export function riskUrgency(entry: ReportDifferential): Urgency | null {
  return entry.risk_level === undefined ? null : (RISK_URGENCY[entry.risk_level] ?? null);
}

const SEVERITY: Record<Urgency, number> = {
  non_urgent: 1,
  semi_urgent: 2,
  urgent: 3,
  emergency: 4,
};

/** The entry that set the session's urgency: the first of the highest risk in the window. */
export type UrgencyDriver = { rank: number; entry: ReportDifferential; urgency: Urgency };

export function urgencyDriver(
  entries: readonly ReportDifferential[] | null | undefined,
): UrgencyDriver | null {
  let driver: UrgencyDriver | null = null;
  const inWindow = (entries ?? []).slice(0, TRIAGE_WINDOW);
  for (const [position, entry] of inWindow.entries()) {
    const urgency = riskUrgency(entry);
    if (urgency !== null && (driver === null || SEVERITY[urgency] > SEVERITY[driver.urgency])) {
      driver = { rank: position + 1, entry, urgency };
    }
  }
  return driver;
}

/**
 * The session's urgency from its report's differentials: the driver's, `non_urgent` when no
 * entry in the window carried a risk, and `unknown` with no report at all.
 */
export function deriveUrgencyFromDifferentials(
  entries: readonly ReportDifferential[] | null | undefined,
): UrgencyLevel {
  if (!entries) return "unknown";
  return urgencyDriver(entries)?.urgency ?? "non_urgent";
}

/**
 * An urgency string narrowed to the four-level acuity scale, or `null` when it is off the
 * scale — `unknown` included, which `Acuity` then renders as an em dash rather than coercing
 * it to a level it never earned.
 */
export function acuityTone(urgency: string | null): Urgency | null {
  switch (urgency) {
    case "non_urgent":
    case "semi_urgent":
    case "urgent":
    case "emergency":
      return urgency;
    default:
      return null;
  }
}
